import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/index.jsx';
import { useTeam } from '../team.js';
import Logo from './Logo.jsx';
import { useContent } from '../content.jsx';

const CONTACT_EMAIL = 'info@pmecanada.com';
const SOCIAL = ['linkedin', 'facebook', 'instagram', 'youtube'];

function SocialIcon({ name }) {
  const p = {
    linkedin: 'M4.98 3.5a2.5 2.5 0 11-.02 5 2.5 2.5 0 01.02-5zM3 9h4v12H3zM9 9h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05C20.6 8.65 21 11.3 21 14.7V21h-4v-5.6c0-1.34-.03-3.06-1.86-3.06-1.87 0-2.16 1.46-2.16 2.96V21H9z',
    facebook: 'M14 8h3V4h-3c-2.76 0-4 1.79-4 4.5V11H7v4h3v9h4v-9h3l1-4h-4V8.6c0-.4.3-.6.6-.6z',
    instagram: 'M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm5 5.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9zm0 2a2.5 2.5 0 110 5 2.5 2.5 0 010-5zM17.5 5.8a1 1 0 100 2 1 1 0 000-2z',
    youtube: 'M23 7.2a3 3 0 00-2.1-2.1C19 4.6 12 4.6 12 4.6s-7 0-8.9.5A3 3 0 001 7.2 31 31 0 00.5 12a31 31 0 00.5 4.8 3 3 0 002.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 002.1-2.1 31 31 0 00.5-4.8 31 31 0 00-.5-4.8zM9.8 15.1V8.9L15.2 12z',
  }[name];
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={p} /></svg>;
}

// Company contact details (Admin > Website content > Contact & footer),
// shown in the footer and on the contact page.
export function ContactDetails({ large }) {
  const { t } = useI18n();
  const { text, value } = useContent('contact');
  const email = value('email', CONTACT_EMAIL);
  const phone = value('phone');
  const address = value('address');
  const hours = text('hours');
  const socials = SOCIAL.filter((s) => value(s));
  return (
    <div className={`contact-details${large ? ' is-large' : ''}`}>
      <p><a className={large ? 'strong' : ''} href={`mailto:${email}`}>{email}</a></p>
      {phone && <p><a href={`tel:${phone.replace(/[^\d+]/g, '')}`}>{phone}</a></p>}
      {address && <p className="pre-line">{address}</p>}
      {hours && <p className="pre-line muted">{hours}</p>}
      {socials.length > 0 && (
        <p className="social-links" aria-label={t('footer.followUs')}>
          {socials.map((s) => <a key={s} href={value(s)} target="_blank" rel="noopener noreferrer" aria-label={t(`footer.social.${s}`)} title={t(`footer.social.${s}`)}><SocialIcon name={s} /></a>)}
        </p>
      )}
    </div>
  );
}

export default function Footer() {
  const { t, lang } = useI18n();
  const team = useTeam();
  const { text } = useContent('contact');
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-brand">
          <Link to="/" className="brand brand-footer">
            <Logo size={34} />
            <span translate="no">{t('brand.name')}</span>
          </Link>
          <p className="muted">{text('footerText', 'footer.tagline')}</p>
          <ContactDetails />
        </div>
        <nav className="footer-col" aria-label={t('footer.company')}>
          <h2>{t('footer.company')}</h2>
          <Link to="/about">{t('nav.about')}</Link>
          {team.inMenu && <Link to={lang === 'en' ? '/team' : '/equipe'}>{t('nav.team')}</Link>}
          <Link to="/news">{t('nav.news')}</Link>
          <Link to="/contact">{t('nav.contact')}</Link>
          <Link to="/help">{t('help.title')}</Link>
        </nav>
        <nav className="footer-col" aria-label={t('footer.members')}>
          <h2>{t('footer.members')}</h2>
          <Link to="/marketplace">{t('nav.marketplace')}</Link>
          <Link to="/prices">{t('prices.title')}</Link>
          <Link to="/signup">{t('nav.signup')}</Link>
          <Link to="/login">{t('nav.login')}</Link>
        </nav>
        <nav className="footer-col" aria-label={t('footer.legal')}>
          <h2>{t('footer.legal')}</h2>
          <Link to="/privacy">{t('footer.privacy')}</Link>
          <Link to="/terms">{t('footer.terms')}</Link>
        </nav>
      </div>
      <div className="container footer-bottom">
        <p className="muted">© {new Date().getFullYear()} <span translate="no">{t('brand.legal')}</span> {t('footer.rights')}</p>
      </div>
    </footer>
  );
}
